/**
 * Fluent v9 theme selection, bound to the Teams host theme (BUILD-SPEC.md
 * §7: "Correct in Teams light, dark, and high-contrast themes via Fluent
 * tokens and the teams-js theme binding").
 *
 * Never import a hardcoded colour anywhere in src/ui/ — always a token from
 * this theme or from `@fluentui/react-components` `tokens`.
 */
import { useEffect, useState } from "react";
import * as microsoftTeams from "@microsoft/teams-js";
import { teamsHighContrastTheme, webDarkTheme, webLightTheme, type Theme } from "@fluentui/react-components";

export type TeamsThemeName = "default" | "dark" | "contrast";

export function themeFor(name: string | undefined): Theme {
  switch (name) {
    case "dark":
      return webDarkTheme;
    case "contrast":
      return teamsHighContrastTheme;
    default:
      return webLightTheme;
  }
}

/**
 * Tracks the Teams host theme live. Outside Teams (unit tests, a plain
 * browser tab during local dev) `getContext`/`registerOnThemeChangeHandler`
 * throw or never resolve — both are treated as "stick with the light theme",
 * never as a crash (BUILD-SPEC.md hard rule 6: degrade, never fail).
 */
export function useTeamsTheme(): Theme {
  const [themeName, setThemeName] = useState<string>("default");

  useEffect(() => {
    let cancelled = false;

    microsoftTeams.app
      .getContext()
      .then((ctx) => {
        if (!cancelled && ctx.app?.theme) setThemeName(ctx.app.theme);
      })
      .catch(() => {
        /* not running inside Teams — default theme stands */
      });

    try {
      microsoftTeams.app.registerOnThemeChangeHandler((next) => {
        if (!cancelled) setThemeName(next);
      });
    } catch {
      /* teams-js not initialized outside Teams — default theme stands */
    }

    return () => {
      cancelled = true;
    };
  }, []);

  return themeFor(themeName);
}
